<template>
  <!-- 商机漏斗：从线索到成交的全流程转化分析 -->
  <div class="opportunity-funnel">
    <list-page style="height: auto;display: block">
      <search-condition @search="loadFunnel" :show-search="true" page-name="opportunityFunnel">
        <template #default>
          <!-- 普通角色只看得到与自己相关的商机，按负责人再筛一次没有意义，直接不显示 -->
          <employee-select v-if="isSuperAdmin" v-model="query.ownerEmployeeId" placeholder="全部" @change="loadFunnel">
            <template #prefix>负责人</template>
          </employee-select>
        </template>
        <template #option>
          <page-button type="read" el-type="primary" @click="loadFunnel">刷新</page-button>
        </template>
      </search-condition>

      <!-- KPI：阶段明细的合计（yq-table 不支持合计行，口径与原合计行一致） -->
      <div v-loading="loading" class="stat-grid">
        <div class="stat-card">
          <div class="label">商机总数</div>
          <div class="value">{{ totalOpportunityCount }}<span class="unit">个</span></div>
          <div class="sub">全部阶段合计（含已流失）</div>
        </div>
        <div class="stat-card">
          <div class="label">商机总金额</div>
          <div class="value money">{{ money(totalOpportunityAmount) }}<span class="unit">元</span></div>
          <div class="sub">全部阶段合计（含已流失）</div>
        </div>
      </div>

      <!-- 漏斗图 + 右侧图例 -->
      <div v-loading="loading" class="chart-card">
        <div class="section-title">销售漏斗</div>
        <div class="funnel-wrap">
          <div ref="funnelChart" class="chart-div"></div>
          <div class="funnel-legend">
            <div v-for="(s, i) in funnelRows" :key="s.stageValue" class="fl-row">
              <span class="fl-name">{{ s.stageTitle }}</span>
              <span class="fl-track">
                <i :style="{width: barWidth(s) + '%', background: funnelColor(i)}"></i>
              </span>
              <span class="fl-val">{{ s.opportunityCount || 0 }}</span>
              <span class="fl-rate">{{ s.convertRate === null || s.convertRate === undefined ? '—' : s.convertRate + '%' }}</span>
            </div>
          </div>
        </div>
      </div>

      <list-table v-loading="loading" background-color="#FFF" style="height: auto">
        <!-- 阶段明细：各阶段的商机构成与金额分布（含「已流失」阶段，漏斗图不含） -->
        <yq-table :data="detailRows" :empty-text="'暂无数据'" height="auto">
          <yq-table-column label="阶段" prop="stageTitle" min-width="200rem">
            <template v-slot="{row, $index}">
              <div class="stage-cell">
                <span class="stage-no" :style="{background: funnelColor($index)}">{{ $index + 1 }}</span>
                <span class="stage-name">{{ row.stageTitle }}</span>
              </div>
            </template>
          </yq-table-column>
          <yq-table-column label="商机数量" prop="opportunityCount" align="right" min-width="110rem">
            <template v-slot="{row}">
              <span class="num">{{ row.opportunityCount || 0 }}</span>
            </template>
          </yq-table-column>
          <yq-table-column label="商机金额" prop="opportunityAmount" align="right" min-width="150rem">
            <template v-slot="{row}">
              <span class="num bold">{{ money(row.opportunityAmount) }}</span>
            </template>
          </yq-table-column>
          <yq-table-column label="金额占比" prop="amountRatio" min-width="180rem">
            <template v-slot="{row}">
              <div class="ratio-cell">
                <span class="ratio-track">
                  <i :style="{width: (row.amountRatio || 0) + '%', background: '#1677ff'}"/>
                </span>
                <span class="ratio-text">{{ row.amountRatio || 0 }}%</span>
              </div>
            </template>
          </yq-table-column>
        </yq-table>
      </list-table>
    </list-page>
  </div>
</template>

<script>
import * as echarts from "echarts";
import {markRaw} from "vue";
import ListPage from "@/components/h5/layout/ListPage.vue";
import ListTable from "@/components/h5/layout/ListTable.vue";
import SearchCondition from "@/components/h5/layout/SearchCondition.vue";
import PageButton from "@/components/h5/button/PageButton.vue";
import EmployeeSelect from "@/components/h5/data/EmployeeSelect.vue";
import YqTable from "@/components/h5/table/YqTable.vue";
import opportunityFunnelApi from "@/api/dz-crm/partner/opportunityFunnelApi";
import {DICT_OPPORTUNITY_STAGE, fillStageRows, stageTagType} from "@/views/h5/crm/crmDictionary";
import commonApi from "@/api/commonApi";
import Util from "@/util/util";
import {useRoute, useRouter} from "vue-router";

// 漏斗配色，由浅到深
const FUNNEL_COLORS = ["#69b1ff", "#4096ff", "#1677ff", "#0958d9", "#003eb3", "#002c8c", "#001f5c"];

export default {
  setup() {
    return {router: useRouter(), route: useRoute()};
  },
  name: "OpportunityFunnel",
  components: {YqTable, ListPage, ListTable, SearchCondition, PageButton, EmployeeSelect},
  data() {
    return {
      loading: false,
      query: {ownerEmployeeId: ""},
      stageStats: [],
      stageDict: [],
      funnelChart: null,
      // 角色分类，1超级管理员 2普通角色。普通角色只看得到与自己相关的商机
      roleType: "",
    };
  },
  beforeRouteLeave(to, from, next) {
    // Util.cachePageData(this, to, from);
    next();
  },
  mounted() {
    this.roleType = Util.getRoleType();
    // 阶段名称走唯一性字典（ba_dictionary_merchant，取值 user_code / 显示 dict_name）
    commonApi.queryUniqueDictionaryList(DICT_OPPORTUNITY_STAGE).then(list => {
      this.stageDict = list || [];
      this.$nextTick(() => this.renderChart());
    });
    this.loadFunnel();
  },
  computed: {
    isSuperAdmin() {
      return this.roleType === 1;
    },
    /**
     * 漏斗层：以字典为准取全部阶段，剔除关闭阶段（已流失），缺数据的阶段补 0，
     * 保证没有商机的阶段也占一层，图形不会因缺层而变形。
     */
    funnelRows() {
      return fillStageRows(this.stageDict, this.stageStats, {excludeClose: true});
    },
    /** 阶段明细：含关闭阶段，补 0 后展示全部阶段 */
    detailRows() {
      return fillStageRows(this.stageDict, this.stageStats);
    },
    /** 阶段明细合计 · 商机数量（yq-table 不支持合计行，改用 KPI 展示） */
    totalOpportunityCount() {
      return this.detailRows.reduce((sum, r) => sum + (r.opportunityCount || 0), 0);
    },
    /** 阶段明细合计 · 商机金额 */
    totalOpportunityAmount() {
      return this.detailRows.reduce((sum, r) => sum + Number(r.opportunityAmount || 0), 0);
    },
  },
  unmounted() {
    if (this.funnelChart) {
      this.funnelChart.dispose();
    }
  },
  methods: {
    async loadFunnel() {
      this.loading = true;
      try {
        const params = {};
        Object.keys(this.query).forEach(k => {
          const v = this.query[k];
          if (v === "" || v === null || v === undefined) {
            return;
          }
          params[k] = v;
        });
        const data = await opportunityFunnelApi.funnel(params) || {};
        this.stageStats = data.stageStats || [];
        this.$nextTick(() => this.renderChart());
      } finally {
        this.loading = false;
      }
    },
    renderChart() {
      const dom = this.$refs.funnelChart;
      if (!dom || !this.funnelRows.length) {
        return;
      }
      if (!this.funnelChart) {
        this.funnelChart = markRaw(echarts.init(dom));
      }
      this.funnelChart.setOption({
        tooltip: {trigger: "item", appendToBody: true, formatter: "{b}：{c}"},
        color: FUNNEL_COLORS,
        series: [{
          type: "funnel",
          left: "10%",
          right: "10%",
          top: 10,
          bottom: 10,
          minSize: "20%",
          sort: "descending",
          label: {show: true, position: "inside", formatter: "{b}"},
          data: this.funnelRows.map(s => ({name: s.stageTitle, value: s.opportunityCount || 0})),
        }],
      }, true);
    },
    /** 图例条宽度，以第一层为 100% */
    barWidth(stat) {
      const first = this.funnelRows.length ? (this.funnelRows[0].opportunityCount || 0) : 0;
      if (!first) {
        return 0;
      }
      return Math.min(100, (stat.opportunityCount || 0) / first * 100).toFixed(1);
    },
    funnelColor(index) {
      return FUNNEL_COLORS[index % FUNNEL_COLORS.length];
    },
    /**
     * 合计行：阶段值等无意义的列留空
     * ★ 保留但不再绑定 —— yq-table 不支持 show-summary，合计已改由顶部 KPI 展示，
     * 计算口径与 KPI 的 totalOpportunityCount / totalOpportunityAmount 一致。
     */
    summaryMethod({columns, data}) {
      return columns.map((col, i) => {
        if (i === 0) {
          return "合计";
        }
        if (col.label === "商机数量") {
          const total = data.reduce((sum, r) => sum + (r.opportunityCount || 0), 0);
          return String(total);
        }
        if (col.label === "商机金额") {
          const total = data.reduce((sum, r) => sum + Number(r.opportunityAmount || 0), 0);
          return this.money(total);
        }
        if (col.label === "金额占比") {
          return "100%";
        }
        return "";
      });
    },
    stageTagType(stageValue) {
      return stageTagType(stageValue);
    },
    money(value) {
      if (value === null || value === undefined) {
        return "0.00";
      }
      return Number(value).toLocaleString("zh-CN", {minimumFractionDigits: 2, maximumFractionDigits: 2});
    },
  },
};
</script>

<style lang="scss" scoped>
.stat-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
  margin-bottom: 14px;

  .stat-card {
    border: 1px solid var(--yq-border-color, #ebeef5);
    border-radius: 6px;
    padding: 12px 16px;
    background: #fff;

    .label {
      font-size: 12px;
      color: #909399;
    }

    .value {
      font-size: 24px;
      font-weight: 700;
      height: 34px;
      line-height: 34px;
      margin: 4px 0;

      &.money {
        font-size: 20px;
      }


      .unit {
        font-size: 14px;
        font-weight: 400;
        margin-left: 2px;
      }
    }

    .sub {
      font-size: 12px;
      color: #909399;
    }
  }
}

.chart-card {
  border: 1px solid var(--yq-border-color, #ebeef5);
  border-radius: 6px;
  padding: 12px 16px;
  margin-bottom: 12px;
  background: #fff;

  .section-title {
    font-size: 14px;
    font-weight: 600;
    color: #303133;
    margin-bottom: 10px;
  }

  .chart-div {
    height: 280px;
    width: 100%;
  }
}

.funnel-wrap {
  display: flex;
  align-items: center;
  gap: 28px;
  flex-wrap: wrap;

  .chart-div {
    flex: 0 0 330px;
    height: 280px;
  }

  .funnel-legend {
    flex: 1;
    min-width: 280px;

    .fl-row {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 6px 0;
      font-size: 13px;

      .fl-name {
        width: 80px;
        color: #303133;
      }

      .fl-track {
        flex: 1;
        height: 10px;
        background: #f0f2f5;
        border-radius: 5px;
        overflow: hidden;

        i {
          display: block;
          height: 100%;
          border-radius: 5px;
        }
      }

      .fl-val {
        width: 60px;
        text-align: right;
        font-weight: 600;
        color: #303133;
      }

      .fl-rate {
        width: 60px;
        text-align: right;
        color: #909399;
      }
    }
  }
}

/* 阶段明细：阶段序号 + 名称 */
.stage-cell {
  display: flex;
  align-items: center;
  gap: 10px;

  .stage-no {
    flex: 0 0 20px;
    width: 20px;
    height: 20px;
    line-height: 20px;
    text-align: center;
    border-radius: 50%;
    color: #fff;
    font-size: 12px;
  }

  .stage-name {
    color: #303133;
    font-weight: 500;
  }
}

.ratio-cell {
  display: flex;
  align-items: center;
  gap: 10px;

  .ratio-track {
    flex: 1;
    height: 6px;
    background: #f0f2f5;
    border-radius: 3px;
    overflow: hidden;

    i {
      display: block;
      height: 100%;
      border-radius: 3px;
      transition: width .3s;
    }
  }

  .ratio-text {
    flex: 0 0 52px;
    text-align: right;
    color: #606266;
    font-variant-numeric: tabular-nums;
  }
}

.num {
  font-variant-numeric: tabular-nums;
}

.bold {
  font-weight: 600;
  color: #303133;
}
</style>
